/** Adapter B1–B3 (lưu qua `PUT /v1/knowledge`). viTri theo các trang hiện có:
 * B1: 1 tầm nhìn, 2 điểm mạnh công ty, 3 yếu tố thành công ngành (VRIO), 4 điểm mạnh đối thủ, 5 đối thủ, 6 lợi thế cạnh tranh
 * B2/B3: 2 S, 11 W, 12 O, 13 T, 14 SO, 15 WO, 16 ST, 17 WT
 */
import { safeConvertOldJsonToNewFormat } from '@/components/DanhGia/ConvertOldJson';
import type { AiProposalItem } from '@/types/ai-agent';
import type { KienThucItem } from '@/types/knowledge';
import { buildVrioNguyenTac, parseVrioNguyenTac } from '@/utils/vrioScore';
import { createKnowledgeAdapter, itemsAt, loadKnowledge, newKnowledgeItem, restoreChanges, type KnowledgeData } from './knowledgeAdapter';
import { AdapterSaveError, type StepAdapter } from './types';
import { getB1SevenSDocumentsForAi, type BscSevenSDocRow } from '@/components/common/AiStepAssistant/bscSevenSContext';
import { guideApi } from '@/services/guide/guide-api';
import { knowledgeApi } from '@/services/knowledge/knowledge-api';
import { useAuthStore } from '@/store/useAuthStore';
import { usePrivateStore } from '@/store/usePrivateStore';
import type { KnowledgeModel } from '@/types/knowledge';

/** Target hướng dẫn/thẻ Gợi ý 7S trên EvaluatePage (điểm mạnh công ty). */
const B1_STRENGTHS_GUIDE_TARGET = '13';

const tenantId = () => useAuthStore.getState().tenantID || '';
const requestHeader = () => usePrivateStore.getState().getRequestHeader();

const B1 = { loadTenBuoc: 'buoc1', loadViTris: [1, 2, 3, 4, 5, 6], saveTenBuoc: 'buoc1', saveViTris: [1, 2, 3, 4, 5, 6] };
const STRATEGY_VITRIS = [2, 6, 11, 12, 13, 14, 15, 16, 17];
const STRATEGY_SECTIONS = {
  SO: { viTri: 14, label: 'Chiến lược SO' },
  WO: { viTri: 15, label: 'Chiến lược WO' },
  ST: { viTri: 16, label: 'Chiến lược ST' },
  WT: { viTri: 17, label: 'Chiến lược WT' },
};

const stripHtml = (html?: string | null) =>
  String(html ?? '')
    .replace(/<\/(p|li|h\d)>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .trim();

/** Kết quả B1 cho các bước sau (đọc bằng API hiện có, quyền người dùng). */
/** Ngữ cảnh B1 gửi kèm hub chat (child success_factor / competitive). */
export const fetchB1ChatUserContext = async (): Promise<Record<string, unknown>> => {
  const { user_context } = await b1Context();
  return user_context as Record<string, unknown>;
};

/** Bubble hub chat — ngữ cảnh theo bước BSC (B1–B5). */
export const fetchHubChatUserContext = async (page?: {
  step?: string;
  route?: string;
}): Promise<Record<string, unknown>> => {
  const step = page?.step;
  const route = page?.route ?? '';
  if (step === 'b1') return fetchB1ChatUserContext();
  if (step === 'b2') {
    const ctx =
      route.includes('chien-luoc') && !route.includes('swot')
        ? await b2StrategyProposeContext()
        : await swotSevenSContext();
    return ctx as Record<string, unknown>;
  }
  if (step === 'b3' || step === 'b4' || step === 'b5') {
    const base = (await b1Context()).user_context as Record<string, unknown>;
    const extra = await stepChatExtras(step).catch(() => ({}));
    return { ...base, industry_name: String(base.industry ?? ''), ...extra };
  }
  return {};
};

/** B3–B5: dữ liệu các bước trước để chat trả lời đúng ngữ cảnh (cùng snapshot với «Tra cứu AI»). */
const stepChatExtras = async (step: string): Promise<Record<string, unknown>> => {
  if (step === 'b3') {
    const snap = b3SelectAdapter.buildSnapshot(await b3SelectAdapter.load());
    return { swot: snap.swot, strategies: snap.strategies };
  }
  const graph = await import('./graphSteps');
  if (step === 'b4') {
    const snap = graph.b4ThemeMapAdapter.buildSnapshot(await graph.b4ThemeMapAdapter.load());
    return { chosen_strategies: snap.strategies, perspectives: snap.perspectives, map_objectives: snap.objectives };
  }
  const snap = graph.b5FishboneAdapter.buildSnapshot(await graph.b5FishboneAdapter.load());
  return { fishbone_objectives: snap.objectives };
};

export const b1Context = async () => {
  const d = await loadKnowledge('buoc1', [1, 2, 3, 4, 5, 6]);
  const vision = d.items.find((i) => i.loaiID === d.loaiByViTri[1]);
  let segments: { HienTai?: string; TuongLai?: string }[] = [];
  let productGroups: { HienTai?: string; TuongLai?: string }[] = [];
  try {
    const json = JSON.parse(vision?.nguyenTac || '{}') as {
      PhanKhucs?: { HienTai?: string; TuongLai?: string }[];
      NhomSanPhams?: { HienTai?: string; TuongLai?: string }[];
    };
    segments = json.PhanKhucs ?? [];
    productGroups = json.NhomSanPhams ?? [];
  } catch {
    /* ignore */
  }
  const plainField = (html?: string | null) => stripHtml(html).replace(/\s+/g, ' ').trim();
  const industryHint = [...segments, ...productGroups]
    .map((p) => [plainField(p.HienTai), plainField(p.TuongLai)].filter(Boolean).join(' → '))
    .filter(Boolean)
    .slice(0, 3)
    .join('; ');
  return {
    user_context: {
      company_name: useAuthStore.getState().tenantID || '',
      industry: industryHint,
      segments,
      product_groups: productGroups,
      company_strengths: itemsAt(d, 2).map((x) => x.text),
      industry_csf: itemsAt(d, 3).map((x) => x.text),
      competitor_strengths: itemsAt(d, 4).map((x) => x.text),
      competitors: itemsAt(d, 5).map((x) => x.text),
    },
    b1: {
      company_strengths: itemsAt(d, 2),
      industry_csf: itemsAt(d, 3),
      competitor_strengths: itemsAt(d, 4),
      competitors: itemsAt(d, 5),
      competitive_advantage: itemsAt(d, 6),
    },
  };
};

/** Parity llm_s1 chat_buoc1_dmct: human_content + documents (mô hình 7S). */
const swotSevenSContext = async () => {
  const base = await b1Context();
  let documents: BscSevenSDocRow[] = getB1SevenSDocumentsForAi();
  if (!documents.length) {
    try {
      const res = (await guideApi.getHuongDanByTarget({
        site: tenantId(),
        target: B1_STRENGTHS_GUIDE_TARGET,
        requestHeader: requestHeader(),
      })) as {
        success?: boolean;
        data?: { modal7S?: Array<Record<string, unknown>>; modal7SGroup?: Array<Record<string, unknown>> };
        modal7S?: Array<Record<string, unknown>>;
        modal7SGroup?: Array<Record<string, unknown>>;
      };
      const guide = res.data ?? res;
      if (res?.success !== false) {
        documents = buildSevenSDocumentsFromGuide(guide.modal7SGroup ?? [], guide.modal7S ?? []);
      }
    } catch {
      /* optional */
    }
  }
  const strengths = (base.b1.company_strengths as Array<{ id: string; text: string }>) ?? [];
  return {
    ...base,
    human_content: strengths.map((row, i) => ({ key: String(i + 1), value: row.text, id: row.id })),
    documents,
    industry_name: String((base.user_context as Record<string, unknown>).industry ?? ''),
  };
};

export const b1VrioPickProposeContext = async () => {
  const base = await b1Context();
  const csf = (base.b1.industry_csf as Array<{ id: string; text: string }>) ?? [];
  const strengths = (base.b1.company_strengths as Array<{ id: string; text: string }>) ?? [];
  return {
    ...base,
    human_content: csf.map((row, i) => ({ key: String(i + 1), value: row.text, id: row.id })),
    data_diem_manh: strengths.map((row, i) => ({ key: String(i + 1), value: row.text, id: row.id })),
    industry_name: String((base.user_context as Record<string, unknown>).industry ?? ''),
  };
};

export const b1StrengthsProposeContext = async () => {
  const base = await b1Context();
  const strengths = (base.b1.company_strengths as Array<{ id: string; text: string }>) ?? [];
  const human_content = strengths.map((row, i) => ({
    key: String(i + 1),
    value: row.text,
    id: row.id,
  }));

  let documents: BscSevenSDocRow[] = getB1SevenSDocumentsForAi();
  if (!documents.length) {
    try {
      const res = (await guideApi.getHuongDanByTarget({
        site: tenantId(),
        target: B1_STRENGTHS_GUIDE_TARGET,
        requestHeader: requestHeader(),
      })) as {
        success?: boolean;
        data?: { modal7S?: Array<Record<string, unknown>>; modal7SGroup?: Array<Record<string, unknown>> };
        modal7S?: Array<Record<string, unknown>>;
        modal7SGroup?: Array<Record<string, unknown>>;
      };
      const guide = res.data ?? res;
      if (res?.success !== false) {
        documents = buildSevenSDocumentsFromGuide(guide.modal7SGroup ?? [], guide.modal7S ?? []);
      }
    } catch {
      /* guide optional */
    }
  }

  return {
    ...base,
    human_content,
    documents,
    industry_name: String((base.user_context as Record<string, unknown>).industry ?? ''),
  };
};

export function buildSevenSDocumentsFromGuide(
  groups: Array<Record<string, unknown>>,
  items: Array<Record<string, unknown>>,
): BscSevenSDocRow[] {
  const rows: BscSevenSDocRow[] = [];
  groups.forEach((group) => {
    const gid = String(group?.id ?? '');
    const gname = String(group?.name ?? group?.Name ?? '').trim();
    if (gname) rows.push({ group: gname, name: '', checked: false, content: '' });
    items
      .filter((it) => String(it?.content ?? it?.Content ?? '') === gid)
      .forEach((it) => {
        const name = String(it?.name ?? it?.Name ?? '').trim();
        const hint = String(it?.noiDung ?? it?.NoiDung ?? it?.description ?? '').trim();
        rows.push({
          group: gname,
          name,
          checked: false,
          content: hint,
        });
      });
  });
  return rows;
}

const strategiesSnapshot = (d: KnowledgeData, onlyChosen = false) =>
  Object.entries(STRATEGY_SECTIONS).flatMap(([section, { viTri }]) =>
    d.items
      .filter((i) => i.loaiID === d.loaiByViTri[viTri] && (!onlyChosen || i.chon))
      .map((i) => {
        const [left = '', right = ''] = (i.ten ?? '').split('+');
        return {
          id: i.id,
          section,
          text: (i.noiDung ?? '').trim(),
          chon: !!i.chon,
          left_ids: left && left !== 'null' ? left.split(',') : [],
          right_ids: right && right !== 'null' ? right.split(',') : [],
          result: stripHtml(i.nguyenTac),
        };
      }),
  );

const swotSnapshot = (d: KnowledgeData) => ({
  strengths: itemsAt(d, 2),
  weaknesses: itemsAt(d, 11),
  opportunities: itemsAt(d, 12),
  threats: itemsAt(d, 13),
  competitive_advantage: itemsAt(d, 6),
});

const listAdapter = (key: string, title: string, viTri: number, section: string, label: string, withB1Context = false) =>
  createKnowledgeAdapter({
    key,
    title,
    ...B1,
    sections: { [section]: { viTri, label } },
    buildContext: withB1Context || key.startsWith('b1.') ? b1Context : undefined,
  });

export const b1StrengthsAdapter = createKnowledgeAdapter({
  key: 'b1.strengths',
  title: 'Điểm mạnh công ty',
  ...B1,
  sections: { company_strengths: { viTri: 2, label: 'Điểm mạnh công ty' } },
  buildContext: b1StrengthsProposeContext,
});
export const b1CompetitorStrengthsAdapter = listAdapter('b1.competitor_strengths', 'Điểm mạnh đối thủ', 4, 'competitor_strengths', 'Điểm mạnh đối thủ', true);
export const b1CompetitorsAdapter = listAdapter('b1.competitors', 'Đối thủ cạnh tranh', 5, 'competitors', 'Đối thủ', true);
export const b1AdvantageAdapter = listAdapter('b1.advantage', 'Lợi thế cạnh tranh', 6, 'competitive_advantage', 'Lợi thế cạnh tranh', true);

/** Chấm VRIO từng CSF (LTCT) — cùng apply CSF như b1.csf, step riêng b1.vrio_pick. */
export const b1VrioPickAdapter = createKnowledgeAdapter({
  key: 'b1.vrio_pick',
  title: 'Lợi thế cạnh tranh · VRIO',
  ...B1,
  sections: { industry_csf: { viTri: 3, label: 'Yếu tố thành công' } },
  buildContext: b1VrioPickProposeContext,
  extraOf: (item) => {
    const v = parseVrioNguyenTac(item.nguyenTac);
    return v ? { vrio: { V: v.V, R: v.R, I: v.I, O: v.O } } : {};
  },
  applyFields: (item, p, edited) => {
    const vrio = p.fields.vrio as { V: number; R: number; I: number; O: number } | undefined;
    const prev = parseVrioNguyenTac(item.nguyenTac);
    return {
      ...item,
      noiDung: edited ?? String(p.fields.text ?? item.noiDung ?? ''),
      nguyenTac: vrio ? buildVrioNguyenTac(vrio.V, vrio.R, vrio.I, vrio.O, prev?.chon) : item.nguyenTac,
    };
  },
  describe: (p, existing) => {
    const vrio = p.fields.vrio as { V: number; R: number; I: number; O: number } | undefined;
    const prev = existing ? parseVrioNguyenTac(existing.nguyenTac) : null;
    const fmt = (v: { V: number; R: number; I: number; O: number } | null | undefined) =>
      v ? ` [V${v.V} R${v.R} I${v.I} O${v.O}]` : '';
    return {
      before: existing ? `${(existing.noiDung ?? '').trim()}${fmt(prev)}` : '',
      after: p.op === 'delete' ? '' : `${String(p.fields.text ?? '')}${fmt(vrio)}`,
    };
  },
});

export const b1CsfAdapter = createKnowledgeAdapter({
  key: 'b1.csf',
  title: 'Yếu tố thành công ngành (VRIO)',
  ...B1,
  sections: { industry_csf: { viTri: 3, label: 'Yếu tố thành công' } },
  buildContext: b1Context,
  extraOf: (item) => {
    const v = parseVrioNguyenTac(item.nguyenTac);
    return v ? { vrio: { V: v.V, R: v.R, I: v.I, O: v.O } } : {};
  },
  applyFields: (item, p, edited) => {
    const vrio = p.fields.vrio as { V: number; R: number; I: number; O: number } | undefined;
    const prev = parseVrioNguyenTac(item.nguyenTac);
    return {
      ...item,
      noiDung: edited ?? String(p.fields.text ?? item.noiDung ?? ''),
      nguyenTac: vrio ? buildVrioNguyenTac(vrio.V, vrio.R, vrio.I, vrio.O, prev?.chon) : item.nguyenTac,
    };
  },
  describe: (p, existing) => {
    const vrio = p.fields.vrio as { V: number; R: number; I: number; O: number } | undefined;
    const prev = existing ? parseVrioNguyenTac(existing.nguyenTac) : null;
    const fmt = (v: { V: number; R: number; I: number; O: number } | null | undefined) =>
      v ? ` [V${v.V} R${v.R} I${v.I} O${v.O}]` : '';
    return {
      before: existing ? `${(existing.noiDung ?? '').trim()}${fmt(prev)}` : '',
      after: p.op === 'delete' ? '' : `${String(p.fields.text ?? '')}${fmt(vrio)}`,
    };
  },
});

/** Tầm nhìn (viTri 1): 1 mục, nguyenTac JSON {QuyMos, ThiPhans, PhanKhucs, NhomSanPhams}; AI chỉ đề xuất phần chữ. */
type Pair = { hien_tai: string; tuong_lai: string };
const pairsText = (pairs: { HienTai?: string; TuongLai?: string }[] | undefined, label: string) =>
  (pairs ?? []).map((p) => `${label}: ${p.HienTai ?? ''} → ${p.TuongLai ?? ''}`).join('\n');

export const b1VisionAdapter: StepAdapter<KnowledgeData> = (() => {
  const base = createKnowledgeAdapter({ key: 'b1.vision', title: 'Bức tranh tầm nhìn', ...B1, sections: { vision: { viTri: 1, label: 'Tầm nhìn' } } });
  const visionOf = (d: KnowledgeData) => d.items.find((i) => i.loaiID === d.loaiByViTri[1]);
  const parse = (item?: KienThucItem) => {
    try {
      return safeConvertOldJsonToNewFormat(JSON.parse(item?.nguyenTac || '{}')) || {};
    } catch {
      return {};
    }
  };
  const toPairs = (list: unknown) => ((list as Pair[]) ?? []).map((x) => ({ HienTai: x.hien_tai, TuongLai: x.tuong_lai }));
  return {
    ...base,
    buildSnapshot: (d) => {
      const json = parse(visionOf(d));
      return {
        step: 'b1.vision',
        vision: { id: visionOf(d)?.id ?? '', phan_khuc: json.PhanKhucs ?? [], nhom_san_pham: json.NhomSanPhams ?? [] },
      };
    },
    diff: (proposal, d) => {
      const json = parse(visionOf(d));
      return proposal.items.map((p: AiProposalItem) => ({
        ref: p.target.ref,
        op: visionOf(d) ? 'update' : 'add',
        sectionLabel: 'Bức tranh tầm nhìn',
        before: [pairsText(json.PhanKhucs, 'Phân khúc'), pairsText(json.NhomSanPhams, 'Nhóm SP')].filter(Boolean).join('\n'),
        after: [pairsText(toPairs(p.fields.phan_khuc), 'Phân khúc'), pairsText(toPairs(p.fields.nhom_san_pham), 'Nhóm SP')]
          .filter(Boolean)
          .join('\n'),
        rationale: p.rationale,
        editable: false,
      }));
    },
    apply: async (proposal, selectedRefs) => {
      const before = await loadKnowledge(B1.loadTenBuoc, B1.loadViTris);
      const p = proposal.items.find((x) => selectedRefs.includes(x.target.ref));
      if (!p) return { savedIds: [], saveEndpoint: 'PUT /v1/knowledge buoc1', undo: async () => undefined };
      const existing = visionOf(before);
      const loaiID = before.loaiByViTri[1];
      if (!loaiID) throw new AdapterSaveError('Không tìm thấy mục Bức tranh tầm nhìn.');
      const json = { QuyMos: [], ThiPhans: [], ...parse(existing) };
      json.PhanKhucs = toPairs(p.fields.phan_khuc);
      json.NhomSanPhams = toPairs(p.fields.nhom_san_pham);
      const item = existing
        ? { ...existing, nguyenTac: JSON.stringify(json), guid: 'update' }
        : { ...newKnowledgeItem(loaiID, 1), nguyenTac: JSON.stringify(json) };
      await saveRaw('buoc1', B1.saveViTris, [item]);
      const after = await loadKnowledge(B1.loadTenBuoc, B1.loadViTris);
      const scope = (d: KnowledgeData) => d.items.filter((i) => i.loaiID === loaiID);
      return {
        savedIds: scope(after).map((i) => i.id),
        saveEndpoint: 'PUT /v1/knowledge buoc1',
        undo: async () => {
          const current = await loadKnowledge(B1.loadTenBuoc, B1.loadViTris);
          await saveRaw('buoc1', B1.saveViTris, restoreChanges(scope(current), scope(before)));
        },
      };
    },
  };
})();

const saveRaw = async (tenBuoc: string, viTris: number[], items: KienThucItem[]) => {
  if (!items.length) return;
  const model = {
    ListKienThucs: items,
    ListMucTieu: [],
    Site: useAuthStore.getState().tenantID || '',
    ViTris: viTris,
    TenBuoc: tenBuoc,
    RequestHeader: usePrivateStore.getState().getRequestHeader(),
  } as KnowledgeModel;
  const res = (await knowledgeApi.update('', model)) as unknown as { success?: boolean; message?: string };
  if (!res || res.success === false) throw new AdapterSaveError(res?.message || 'Lưu dữ liệu thất bại.');
};

export const b2SwotAdapter = createKnowledgeAdapter({
  key: 'b2.swot',
  title: 'Phân tích SWOT',
  loadTenBuoc: 'buoc2swot',
  loadViTris: [2, 11, 12, 13],
  saveTenBuoc: 'buoc2swot',
  saveViTris: [2, 11, 12, 13],
  sections: {
    strengths: { viTri: 2, label: 'Điểm mạnh' },
    weaknesses: { viTri: 11, label: 'Điểm yếu' },
    opportunities: { viTri: 12, label: 'Cơ hội' },
    threats: { viTri: 13, label: 'Thách thức' },
  },
  buildContext: swotSevenSContext,
});

const tenOf = (links: Record<string, unknown>) => {
  const left = ((links.left_ids as string[]) ?? []).join(',');
  const right = ((links.right_ids as string[]) ?? []).join(',');
  return `${left}${right ? `+${right}` : ''}`;
};

const b2StrategyProposeContext = async () => {
  const b1 = await b1Context();
  const advantage = (b1.b1?.competitive_advantage ?? []) as { text?: string; id?: string }[];
  return {
    ...b1,
    industry_name: String((b1.user_context as Record<string, unknown>).industry ?? ''),
    competitive_advantage: advantage.map((x) => ({ id: x.id, text: (x.text ?? '').trim() })).filter((x) => x.text),
  };
};

export const b2StrategyAdapter = createKnowledgeAdapter({
  key: 'b2.strategy',
  title: 'Xây dựng chiến lược',
  loadTenBuoc: 'buoc2chienluoc',
  loadViTris: STRATEGY_VITRIS,
  saveTenBuoc: 'buoc2chienluoc',
  saveViTris: [6, 14, 15, 16, 17],
  sections: STRATEGY_SECTIONS,
  buildContext: b2StrategyProposeContext,
  extraOf: (item) => {
    const [left = '', right = ''] = (item.ten ?? '').split('+');
    return { left_ids: left ? left.split(',') : [], right_ids: right ? right.split(',') : [] };
  },
  extraSnapshot: (d) => ({ swot: swotSnapshot(d) }),
  applyFields: (item, p, edited) => ({
    ...item,
    noiDung: edited ?? String(p.fields.html ?? p.fields.text ?? item.noiDung ?? ''),
    ten: p.links?.left_ids || p.links?.right_ids ? tenOf(p.links) : item.ten,
  }),
  describe: (p) => {
    const n = ((p.links?.left_ids as string[]) ?? []).length + ((p.links?.right_ids as string[]) ?? []).length;
    const title = String(p.fields.text ?? '').trim();
    const html = String(p.fields.html ?? '').trim();
    const linkNote = n && p.op !== 'delete' ? ` (liên kết ${n} mục SWOT)` : '';
    return {
      after: (title || stripHtml(html).slice(0, 120) || 'Chiến lược') + linkNote,
      htmlAfter: html || undefined,
      editable: true,
    };
  },
});

export const b3SelectAdapter = createKnowledgeAdapter({
  key: 'b3.select',
  title: 'Lựa chọn chiến lược',
  loadTenBuoc: 'buoc3luachon',
  loadViTris: STRATEGY_VITRIS,
  saveTenBuoc: 'buoc3luachon',
  saveViTris: [6, 14, 15, 16, 17],
  sections: { ...STRATEGY_SECTIONS, strategy: { viTri: -1, label: 'Chiến lược' } },
  extraSnapshot: (d) => ({ strategies: strategiesSnapshot(d), swot: swotSnapshot(d) }),
  applyFields: (item, p) => ({ ...item, chon: Boolean(p.fields.chon) }),
  describe: (p, existing) => ({
    sectionLabel: 'Chiến lược',
    before: existing ? `${existing.chon ? '☑ Đang chọn' : '☐ Chưa chọn'} — ${(existing.noiDung ?? '').trim()}` : '',
    after: `${p.fields.chon ? '☑ Chọn' : '☐ Bỏ chọn'} — ${(existing?.noiDung ?? '').trim()}`,
    editable: false,
  }),
});

const b3ResultProposeContext = async () => {
  const b1 = await b1Context();
  return {
    ...b1,
    industry_name: String((b1.user_context as Record<string, unknown>).industry ?? ''),
  };
};

export const b3ResultAdapter = createKnowledgeAdapter({
  key: 'b3.result',
  title: 'Kết quả chiến lược',
  loadTenBuoc: 'buoc3ketqua',
  loadViTris: STRATEGY_VITRIS,
  saveTenBuoc: 'buoc3ketqua',
  saveViTris: STRATEGY_VITRIS,
  sections: { ...STRATEGY_SECTIONS, strategy: { viTri: -1, label: 'Chiến lược' } },
  buildContext: b3ResultProposeContext,
  extraSnapshot: (d) => ({ strategies: strategiesSnapshot(d, true) }),
  applyFields: (item, p) => ({ ...item, nguyenTac: String(p.fields.result_html ?? item.nguyenTac ?? '') }),
  describe: (p, existing) => {
    const html = String(p.fields.result_html ?? '');
    return {
      sectionLabel: `Kết quả: ${(existing?.noiDung ?? '').trim().slice(0, 80)}`,
      before: stripHtml(existing?.nguyenTac),
      after: stripHtml(html).slice(0, 200) || 'Bảng kết quả kỳ vọng',
      htmlAfter: html || undefined,
      editable: false,
    };
  },
});
